import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Avatar,
  Chip,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  VolumeUp as VolumeUpIcon,
} from "@mui/icons-material";
import {
  useGetPartsQuery,
} from "@/store/api/adminApi";
import PartEditDialog from "./PartEditDialog";

const PartManagement = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPart, setEditingPart] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [partToDelete, setPartToDelete] = useState(null);
  const [imageViewOpen, setImageViewOpen] = useState(false);
  const [imageUrlToView, setImageUrlToView] = useState("");
  const [playingAudio, setPlayingAudio] = useState(null);

  // API Hooks
  const { data: partsData, isLoading, error, refetch } = useGetPartsQuery();

  const parts = partsData?.data || [];


  const handleOpenDialog = (part = null) => {
    if (part) {
      setEditingPart(part);
    } else {
      setEditingPart(null);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingPart(null);
  };

  const handleOpenImageView = (url) => {
    setImageUrlToView(url);
    setImageViewOpen(true);
  };

  const handleAudioPlay = (audioUrl) => {
    setPlayingAudio(audioUrl === playingAudio ? null : audioUrl);
  };

  const handleCloseImageView = () => {
    setImageViewOpen(false);
    setImageUrlToView("");
  };


  const handleDeleteClick = (part) => {
    setPartToDelete(part);
    setDeleteDialogOpen(true);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" sx={{ color: "#d32f2f", fontWeight: "bold" }}>
          Part Management
        </Typography>
        {/* <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          New Part
        </Button> */}
      </Box>

      {isLoading && <CircularProgress />}
      {error && <Alert severity="error">Failed to load parts.</Alert>}

      <Grid container spacing={3}>
        {parts.map((part) => (
          <Grid item xs={12} sm={6} md={4} key={part._id}>
            <Card>
              <CardContent>
                <Typography variant="h6">
                  Part {part.partNumber}: {part.title}
                </Typography>
                <Chip label={part.section} size="small" sx={{ my: 1 }} />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ maxHeight: 100, overflow: "hidden", my: 1 }}
                >
                  {part.instructions}
                </Typography>
                <Box
                  sx={{ display: "flex", alignItems: "center", mt: 2, gap: 2 }}
                >
                  {part.imageUrl && (
                    <IconButton
                      onClick={() => handleOpenImageView(part.imageUrl)}
                    >
                      <Avatar src={part.imageUrl} variant="rounded">
                        <ImageIcon />
                      </Avatar>
                    </IconButton>
                  )}
                  {/* Preview audio */}
                  {part.audioUrl && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <IconButton
                        onClick={() => handleAudioPlay(part.audioUrl)}
                        size="small"
                        color={
                          playingAudio === part.audioUrl
                            ? "primary"
                            : "default"
                        }
                      >
                        <VolumeUpIcon />
                      </IconButton>
                    {playingAudio === part.audioUrl && (
                      <audio
                        src={part.audioUrl}
                        autoPlay
                        onEnded={() => setPlayingAudio(null)}
                        style={{ display: "none" }}
                      />
                    )}
                    </Box>
                  )}
                </Box>
              </CardContent>
              <CardActions>
                <IconButton onClick={() => handleOpenDialog(part)}>
                  <EditIcon />
                </IconButton>
                {/* <IconButton onClick={() => handleDeleteClick(part)}>
                  <DeleteIcon />
                </IconButton> */}
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Upsert Dialog */}
      <PartEditDialog
        dialogOpen={dialogOpen}
        handleCloseDialog={handleCloseDialog}
        part={editingPart}
      />

      {/* Image View Dialog */}
      <Dialog open={imageViewOpen} onClose={handleCloseImageView} maxWidth="md">
        <DialogContent sx={{ p: 1 }}>
          <img
            src={imageUrlToView}
            alt="Part Image"
            style={{ width: "100%", height: "auto" }}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{partToDelete?.title}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PartManagement;
